'use client'

import React, { FC, ReactElement } from 'react'
import styles from './CreateWallet.module.css'
import classNames from 'classnames'

import CreateWalletWelcome from './create-wallet-welcome'
import CreateWalletPassword from './create-wallet-password'
import CreateWalletSeed from './create-wallet-seed'
import CreateWalletConfirm from './create-wallet-confirm'
import CreateWalletSuccess from './create-wallet-success'
import useWindowSize from '@/utils/hooks/useWindowSize'
import { useRouter } from 'next/navigation'
import { CREATE_WALLET_STEPS } from './constants'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import { setPassword } from '@/utils/password'

const TOTAL_STEPS = CREATE_WALLET_STEPS?.length

const CreateWallet: FC = (): ReactElement => {
	const [step, setStep] = React.useState(0)
	const [seed, setSeed] = React.useState('')
	// Held in memory only until the backup check passes. Nothing is written to
	// storage before the user has proven they saved the phrase.
	const [password, setPasswordValue] = React.useState('')
	const [error, _setError] = React.useState(false)
	const [creating, setCreating] = React.useState(false)
	const { isMobile } = useWindowSize()

	const router = useRouter()
	const loggedIn = useLoggedIn()

	// Bounce a browser that already has a wallet, but only on the welcome
	// screen. The logged-in flag revalidates on every window focus, and once the
	// new wallet is persisted it reads true — redirecting here mid-flow used to
	// skip the recovery-phrase backup and its check.
	React.useEffect(() => {
		if (loggedIn && step === 0) {
			router.push('/wallet')
		}
	}, [loggedIn, step, router])

	const next = React.useCallback(() => {
		setStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1))
	}, [])

	const goBack = React.useCallback(() => {
		setStep((prev) => Math.max(prev - 1, 0))
	}, [])

	const handleCreateWallet = React.useCallback(async () => {
		if (creating) {
			return
		}

		setCreating(true)
		try {
			const { generateMnemonic } = await import('@/utils/bitcoin')
			const s = await generateMnemonic()
			setSeed(s)
			next()
		} finally {
			setCreating(false)
		}
	}, [creating, next])

	const handlePassword = React.useCallback(
		(pass: string) => {
			setPasswordValue(pass)
			next()
		},
		[next]
	)

	// Runs only after words 3, 6, 9 and 12 were picked correctly. This is the
	// first and only point where the wallet lands in storage.
	const handleConfirmed = React.useCallback(async () => {
		await setPassword(password, seed)
		next()
	}, [password, seed, next])

	return (
		<div className={classNames(styles.root, { [styles.desktop]: !isMobile })}>
			{step === 0 && (
				<CreateWalletWelcome
					step={step}
					totalSteps={TOTAL_STEPS}
					onNext={handleCreateWallet}
					error={error}
				/>
			)}
			{seed && step === 1 && (
				<CreateWalletPassword
					seed={seed}
					initialPassword={password}
					step={step}
					totalSteps={TOTAL_STEPS}
					onBack={goBack}
					onPassword={handlePassword}
				/>
			)}
			{seed && step === 2 && (
				<CreateWalletSeed
					step={step}
					seed={seed}
					totalSteps={TOTAL_STEPS}
					onBack={goBack}
					onNext={next}
				/>
			)}
			{seed && step === 3 && (
				<CreateWalletConfirm
					step={step}
					seed={seed}
					totalSteps={TOTAL_STEPS}
					onBack={goBack}
					onConfirm={handleConfirmed}
				/>
			)}
			{step === 4 && <CreateWalletSuccess step={step} totalSteps={TOTAL_STEPS} onBack={goBack} />}
		</div>
	)
}

export default CreateWallet
